import {
  EventFeedRequest,
  EventFeedRequestV2,
  EventFeedResponse,
  EventFeedResponseV3,
  eventFeed,
} from '@api';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { wretchRequest } from '@terminal/utils/api-fetch/apiFetch';
import { optimisticQueriesUpdate } from '@terminal/utils/optimistic-updates/optimistic-updates';
import { useEffect } from 'react';
import {
  enrichEventsWithMarketOverviewInsights,
  fetchMarketOverviewInsights,
  mapEventFeedResponse,
  transformEventFeedBody,
} from './utils';

export const QUERY_KEY_EVENT_FEED = 'QUERY_KEY_EVENT_FEED';

interface PageParam {
  page: number;
  next?: string;
  prev?: string;
}

const initialPageParam: PageParam = { page: 1 };

export interface UseEventFeedOptions {
  enabled?: boolean;
}

export const useEventFeed = (
  body: EventFeedRequestV2,
  options?: UseEventFeedOptions
) => {
  const isFollowedThemesFlagEnabled = useFlags(state =>
    state.getFeatureFlagState('followed_themes')
  );
  const isCatalystGenerationEnabled = useFlags(state =>
    state.getFeatureFlagState('catalyst_generation')
  );

  return useInfiniteQuery({
    queryKey: [
      QUERY_KEY_EVENT_FEED,
      body,
      isFollowedThemesFlagEnabled,
      isCatalystGenerationEnabled,
    ],
    enabled: options?.enabled ?? true,
    queryFn: async ({ signal, pageParam }) => {
      const path = isCatalystGenerationEnabled
        ? eventFeed.postFeedEventsV3.path
        : isFollowedThemesFlagEnabled
          ? eventFeed.postFeedEventsV2.path
          : eventFeed.postFeedEvents.path;
      const schema = isCatalystGenerationEnabled
        ? //ignore schema for now as there are type issues
          undefined
        : isFollowedThemesFlagEnabled
          ? eventFeed.postFeedEventsV2.schema
          : eventFeed.postFeedEvents.schema;

      const requestBody = isCatalystGenerationEnabled
        ? transformEventFeedBody(body)
        : body;

      const eventFeedResponse = await wretchRequest<
        EventFeedResponse | EventFeedResponseV3
      >(path, {
        method: 'post',
        body: { ...requestBody, ...pageParam },
        schema,
        signal,
      });

      const response = isCatalystGenerationEnabled
        ? mapEventFeedResponse(eventFeedResponse as EventFeedResponseV3)
        : (eventFeedResponse as EventFeedResponse);

      const hasMarketOverviewEvents = response.events.some(
        event => event.type === 'market-overview'
      );

      if (!hasMarketOverviewEvents) {
        return response;
      }

      const marketOverviewData = await fetchMarketOverviewInsights(
        pageParam.page,
        signal
      );

      const enrichedEvents = enrichEventsWithMarketOverviewInsights(
        response.events,
        marketOverviewData
      );

      return {
        ...response,
        events: enrichedEvents,
      };
    },
    staleTime: 0,
    gcTime: 0,
    initialPageParam,
    getNextPageParam: (
      lastPage,
      _allPages,
      lastPageParam
    ): PageParam | undefined =>
      lastPage?.next_cursor
        ? { next: lastPage.next_cursor, page: lastPageParam.page + 1 }
        : undefined,
    getPreviousPageParam: (
      firstPage,
      _allPages,
      firstPageParam
    ): PageParam | undefined =>
      firstPage?.prev_cursor
        ? { prev: firstPage.prev_cursor, page: firstPageParam.page - 1 }
        : undefined,
  });
};

export const useEventFeedAllPages = (
  body: EventFeedRequest,
  options?: UseEventFeedOptions
) => {
  const result = useEventFeed(body, options);

  const lastPage = result.data?.pages?.at(-1);
  const lastEvent = lastPage?.events?.at(-1);
  const reachedDateBoundary =
    !!body.date_from &&
    !!lastEvent &&
    new Date(lastEvent.created_at) < new Date(body.date_from);

  useEffect(() => {
    if (
      result.hasNextPage &&
      !result.isFetchingNextPage &&
      !reachedDateBoundary
    ) {
      result.fetchNextPage();
    }
  }, [
    result.hasNextPage,
    result.isFetchingNextPage,
    result.fetchNextPage,
    reachedDateBoundary,
  ]);

  return result;
};

/**
 * @deprecated
 * all events return read false
 * https://toggleai.atlassian.net/browse/PLATFORM-3628
 */
export const postEventRead = (eventId: string) => {
  const { path } = eventFeed.postEventRead;
  return wretchRequest(path(eventId), {
    method: 'post',
  });
};

/**
 * @deprecated
 * all events return read false
 * https://toggleai.atlassian.net/browse/PLATFORM-3628
 * Hook for marking events as read with optimistic updates
 * Immediately updates the UI to show the event as read, then sends the API request
 */
export const useMarkEventRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (eventId: string) => postEventRead(eventId),
    onMutate: async (eventId: string) => {
      const { previousData: previousQueries } = await optimisticQueriesUpdate<{
        pages: EventFeedResponse[];
        pageParams: PageParam[];
      }>({
        queryClient,
        queryKey: [QUERY_KEY_EVENT_FEED],
        updateFn: oldData => ({
          ...oldData,
          pages: oldData.pages.map(page => ({
            ...page,
            events: page.events.map(event =>
              event.id === eventId ? { ...event, read: true } : event
            ),
          })),
        }),
      });

      return { previousQueries };
    },
    onError: (_err, _eventId, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
};
